/**
 * Business Impact Analysis Component
 * Shows legal compliance risks, affected user groups, and business implications
 */

import { useMemo } from 'react';
import { AlertTriangle, Scale, Users, Shield, CheckCircle } from 'lucide-react';

interface IssueCounts {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
  total: number;
}

interface BusinessImpactProps {
  totalIssues: IssueCounts;
  overallScore: number;
  eaaScore?: number;
}

const REGULATIONS = [
  {
    name: 'European Accessibility Act (EAA)',
    desc: 'EU directive requiring accessible digital products and services. Deadline: June 28, 2025.',
  },
  {
    name: 'EN 301 549',
    desc: 'European standard defining accessibility requirements for ICT products and services.',
  },
  {
    name: 'WCAG 2.1 Level AA',
    desc: 'International standard for web accessibility, referenced by most accessibility laws.',
  },
  {
    name: 'UK Equality Act 2010',
    desc: 'Requires reasonable adjustments for disabled users accessing digital services.',
  },
];

export function BusinessImpact({ totalIssues, overallScore, eaaScore = 0 }: BusinessImpactProps) {
  // Calculate risk level
  const riskLevel = useMemo(() => {
    if (totalIssues.critical > 0) return 'High';
    if (totalIssues.serious > 0) return 'Medium';
    return 'Low';
  }, [totalIssues]);

  const riskDescription = useMemo(() => {
    if (totalIssues.critical > 0) {
      return 'Critical accessibility barriers may expose your organization to legal action under disability discrimination laws.';
    }
    if (totalIssues.serious > 0) {
      return 'Serious accessibility issues could result in complaints and may require remediation under accessibility regulations.';
    }
    return 'Minor accessibility issues present low legal risk but should still be addressed for best practices.';
  }, [totalIssues]);

  // Calculate affected users percentage
  const affectedPercentage = useMemo(() => {
    const weightedIssues =
      totalIssues.critical * 4 +
      totalIssues.serious * 2 +
      totalIssues.moderate * 1 +
      totalIssues.minor * 0.5;
    const scoreImpact = (100 - overallScore) / 100;
    let percentage = 15 + Math.min(weightedIssues * 0.5, 25) + scoreImpact * 10;
    return Math.round(Math.min(percentage, 50));
  }, [totalIssues, overallScore]);

  // Calculate user group impacts
  const userGroups = useMemo(() => {
    const getImpact = () => {
      if (totalIssues.critical > 0) return 'High';
      if (totalIssues.serious > 2) return 'High';
      if (totalIssues.serious > 0) return 'Medium';
      if (totalIssues.moderate > 3) return 'Medium';
      return 'Low';
    };

    return [
      {
        group: 'Screen Reader Users',
        impact: getImpact(),
        desc: 'Blind and low vision users who rely on screen readers',
        icon: '👁️',
      },
      {
        group: 'Keyboard-Only Users',
        impact: getImpact(),
        desc: 'Users who cannot use a mouse due to motor impairments',
        icon: '⌨️',
      },
      {
        group: 'Low Vision Users',
        impact: getImpact(),
        desc: 'Users who rely on zoom, high contrast, or screen magnification',
        icon: '🔍',
      },
      {
        group: 'Cognitive Disabilities',
        impact: getImpact(),
        desc: 'Users who benefit from clear structure and navigation',
        icon: '🧠',
      },
    ];
  }, [totalIssues]);

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'High':
        return 'text-red-600';
      case 'Medium':
        return 'text-amber-500';
      case 'Low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getRiskBgColor = (level: string) => {
    switch (level) {
      case 'High':
        return 'bg-red-50 border-red-200';
      case 'Medium':
        return 'bg-amber-50 border-amber-200';
      case 'Low':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-heading-2 font-semibold text-dark-navy mb-2">Business Impact Analysis</h2>
        <p className="text-body text-gray-medium">
          Potential business impact of the accessibility issues found, including legal risks and affected user groups.
        </p>
      </div>

      {/* EAA Compliance Alert */}
      <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4">
        <div className="flex items-start gap-3">
          <Scale className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-dark-navy mb-1">
              European Accessibility Act (EAA) Compliance
            </h3>
            <p className="text-body-sm text-gray-dark">
              Your current EAA compliance score is <span className="font-semibold text-coral">{eaaScore}%</span>.
              The European Accessibility Act (Directive 2019/882) requires digital products and services to meet
              EN 301 549 accessibility standards by June 2025.
            </p>
          </div>
        </div>
      </div>

      {/* Risk Level Card */}
      <div className={`card border ${getRiskBgColor(riskLevel)}`}>
        <div className="flex items-center gap-3 mb-3">
          {riskLevel === 'High' ? (
            <AlertTriangle className="w-6 h-6 text-red-600" />
          ) : riskLevel === 'Medium' ? (
            <Shield className="w-6 h-6 text-amber-500" />
          ) : (
            <CheckCircle className="w-6 h-6 text-green-600" />
          )}
          <div>
            <span className="text-body text-gray-dark">Current Risk Level: </span>
            <span className={`font-bold text-lg ${getRiskColor(riskLevel)}`}>{riskLevel}</span>
          </div>
        </div>
        <p className="text-body-sm text-gray-dark">{riskDescription}</p>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Applicable Regulations */}
        <div className="card">
          <h3 className="text-heading-3 font-semibold text-dark-navy mb-4 flex items-center gap-2">
            <Scale className="w-5 h-5 text-coral" />
            Applicable Regulations
          </h3>
          <div className="space-y-3">
            {REGULATIONS.map((reg) => (
              <div key={reg.name} className="border-l-2 border-gray-200 pl-3">
                <p className="font-medium text-dark-navy text-body-sm">{reg.name}</p>
                <p className="text-body-sm text-gray-medium">{reg.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* User Experience Impact */}
        <div className="card">
          <h3 className="text-heading-3 font-semibold text-dark-navy mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-coral" />
            User Experience Impact
          </h3>

          {/* Affected Users */}
          <div className="bg-gray-light rounded-lg p-4 mb-4">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-body text-gray-dark">Estimated Affected Users:</span>
              <span className="text-2xl font-bold text-coral">{affectedPercentage}%</span>
            </div>
            <p className="text-body-sm text-gray-medium">
              Based on issue types and severity, considering users with various disabilities.
            </p>
          </div>

          {/* User Group Impact */}
          <div className="space-y-2">
            {userGroups.map((ug) => (
              <div key={ug.group} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{ug.icon}</span>
                  <div>
                    <p className="font-medium text-body-sm text-dark-navy">{ug.group}</p>
                    <p className="text-body-sm text-gray-medium">{ug.desc}</p>
                  </div>
                </div>
                <span className={`font-semibold text-body-sm ${getRiskColor(ug.impact)}`}>
                  {ug.impact}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
